import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import {
  increaseTrainingPlanPage,
  setHasMoreListings,
  setIsLoadingLoadMoreBtn,
  setIsLoadingTrainingPlans,
  setTrainingPlanPage,
} from "../../../../redux/pages/exploreSlice";
import { ListingsFromApi } from "../../../../interfaces/training-plan/ListingsFromApi";
import { getListings } from "../../../../api/listings";
import { Stack } from "@mui/material";
import { MaterialCard } from "../cards/MaterialCard";
import { MaterialCardLoading } from "../cards/MaterialCardLoading";
import { MOCK_ARRAY_10 } from "../../../../lib/constants";
import { ExploreFiltersPage } from "../exploreFilters/ExploreFiltersPage";
import { ExploreTrainingPlanNoResults } from "./ExploreTrainingPlanNoResults";
import { LoadingSpin } from "../../../global/loading/LoadingSpin";

export const ExploreTraininingPlanSection = () => {
  const [listings, setListings] = useState<ListingsFromApi>();
  const exploreRedux = useSelector((state: RootState) => state.explore.value);
  const listingFilters = useSelector(
    (state: RootState) => state.filters.listings,
  );
  const dispatch = useDispatch();
  const observer = useRef<IntersectionObserver>();

  const handleGetMoreListings = useCallback(
    () =>
      getListings(
        listingFilters.queryStringFilter,
        exploreRedux.pages.trainingPlan.page + 1,
      )
        .then((resListings) => {
          setListings({
            count: resListings.count,
            rows: [...listings.rows, ...resListings.rows],
          });
          dispatch(setIsLoadingLoadMoreBtn(false));
          dispatch(increaseTrainingPlanPage());

          if (resListings.rows.length === 0) {
            dispatch(setHasMoreListings(false));
          }
        })
        .catch((e: Error) => {
          dispatch(setIsLoadingLoadMoreBtn(false));
          console.error(e);
        }),
    [
      dispatch,
      exploreRedux.pages.trainingPlan.page,
      listingFilters.queryStringFilter,
      listings?.rows,
    ],
  );

  const lastListingRef = useCallback(
    (node: HTMLDivElement) => {
      if (exploreRedux.pages.trainingPlan.isLoadingLoadMoreBtn) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          exploreRedux.pages.trainingPlan.hasMore
        ) {
          dispatch(setIsLoadingLoadMoreBtn(true));

          void handleGetMoreListings();
        }
      });

      if (node) observer.current.observe(node);
    },
    [
      exploreRedux.pages.trainingPlan.isLoadingLoadMoreBtn,
      exploreRedux.pages.trainingPlan.hasMore,
      handleGetMoreListings,
      dispatch,
    ],
  );

  useEffect(() => {
    dispatch(setIsLoadingTrainingPlans(true));
    dispatch(setHasMoreListings(true));
    dispatch(setTrainingPlanPage(0));

    getListings(listingFilters.queryStringFilter, 0)
      .then((resListings) => {
        setListings(resListings);
        dispatch(setIsLoadingTrainingPlans(false));
      })
      .catch((e: Error) => {
        console.error(e);
      });
  }, [dispatch, listingFilters.queryStringFilter]);

  return (
    <>
      <ExploreFiltersPage filterFor="listings" />

      <Stack
        className="w-full grid grid-col-1  sm:grid-cols-2 
      md:grid-cols-3  gap-3 
      md:gap-x-4 gap-y-6 lg:gap-6 "
      >
        {!exploreRedux.pages.trainingPlan.isLoadingItems
          ? listings?.rows.map((item, i) => {
              if (i + 1 === listings.rows.length) {
                return (
                  <div ref={lastListingRef} key={i}>
                    <MaterialCard data={item} />
                  </div>
                );
              }

              return <MaterialCard data={item} key={i} />;
            })
          : MOCK_ARRAY_10.map((item) => {
              return <MaterialCardLoading key={item} />;
            })}
      </Stack>

      {exploreRedux.pages.trainingPlan.isLoadingLoadMoreBtn && <LoadingSpin />}

      {listings?.rows.length === 0 &&
        !exploreRedux.pages.trainingPlan.isLoadingItems && (
          <ExploreTrainingPlanNoResults />
        )}
    </>
  );
};
